import React, { useState, useEffect, useRef } from 'react';
import { getStoredConsent, storeConsent, updateConsent, sendPageView } from '../lib/analytics';

/**
 * CookieBanner — GDPR & ePrivacy compliant consent banner.
 *
 * - Shows only when no stored consent exists (or consent version changed)
 * - Accept: grants all Consent Mode v2 signals + sends initial page_view
 * - Decline: keeps all signals denied
 * - Keyboard accessible: focus moves to Decline on open
 * - Equal visual prominence for Accept and Decline
 */
export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const declineBtnRef = useRef(null);

  useEffect(() => {
    // Show banner only if no valid stored consent
    const stored = getStoredConsent();
    if (!stored) {
      setIsVisible(true);
    }
  }, []);

  // Move focus into the banner when it appears (accessibility)
  useEffect(() => {
    if (isVisible && declineBtnRef.current) {
      declineBtnRef.current.focus();
    }
  }, [isVisible]);

  const handleAccept = () => {
    storeConsent('accepted');
    updateConsent(true);
    sendPageView(); // send the page_view that was held back by consent mode
    setIsVisible(false);
  };

  const handleDecline = () => {
    storeConsent('declined');
    updateConsent(false); // keeps all signals denied
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9998,
        maxWidth: '440px',
        width: 'calc(100vw - 48px)',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        border: '1px solid #e5e7eb',
        padding: '24px',
        fontFamily: 'inherit',
        animation: 'cookieBannerSlideIn 0.3s ease-out',
      }}
    >
      <style>{`
        @keyframes cookieBannerSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cookie-btn {
          flex: 1;
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: 1.5px solid transparent;
          transition: background 0.15s, opacity 0.15s;
        }
        .cookie-btn-accept {
          background: #111;
          color: #fff;
          border-color: #111;
        }
        .cookie-btn-accept:hover { opacity: 0.85; }
        .cookie-btn-decline {
          background: #f3f4f6;
          color: #222;
          border-color: #d1d5db;
        }
        .cookie-btn-decline:hover { background: #e5e7eb; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <h2
            id="cookie-banner-title"
            style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 700, color: '#111' }}
          >
            Cookie Preferences
          </h2>
          <p
            id="cookie-banner-desc"
            style={{ margin: 0, fontSize: '13px', color: '#555', lineHeight: 1.6 }}
          >
            We use analytics cookies to understand how visitors use our site.
            No cookies are set before you choose.{' '}
            <a
              href="/cookie-policy"
              style={{ color: '#111', fontWeight: 600 }}
            >
              Cookie Policy
            </a>
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="cookie-btn cookie-btn-accept"
            onClick={handleAccept}
            aria-label="Accept analytics cookies"
          >
            Accept
          </button>
          <button
            ref={declineBtnRef}
            className="cookie-btn cookie-btn-decline"
            onClick={handleDecline}
            aria-label="Decline non-essential cookies"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}
